// Owner membership detail section (SLICE-MEMBERSHIP-PASS). Rendered inside the
// customer-detail modal when the opened card has a `membership_context`. Shows
// the read-only membership facts (tier, member number, status, benefits, total
// check-ins, last visit) plus owner controls: change tier (PATCH) and renew
// the term (POST, gracefully handles the not-renewable 422).
//
// The Recover-pass control is NOT here — it's the card-agnostic action the
// CustomersPage already owns and keeps working for membership cards too.

import { useState } from 'react'
import { toast } from 'sonner'

import { OwnerApiError } from '../ownerApi.js'
import {
  useChangeMembershipTier,
  useRenewMembership,
} from '../hooks/useMembership.js'
import { StatusPill } from '../../shared/membership/MembershipVerifyPanel.js'
import {
  formatExpiry,
  relativeTimeAgo,
  type MembershipContext,
} from '../../shared/membership/membershipTypes.js'
import '../../shared/membership/membership.css'

export function MembershipDetailSection({
  cardId,
  ctx,
}: {
  cardId: string
  ctx: MembershipContext
}) {
  const changeTier = useChangeMembershipTier(cardId)
  const renew = useRenewMembership(cardId)

  const [tierDraft, setTierDraft] = useState(ctx.tier_name ?? '')
  const [renewDisabled, setRenewDisabled] = useState(false)

  const lastVisit = relativeTimeAgo(ctx.last_check_in_at)
  const expiry = formatExpiry(ctx.expires_at)

  async function saveTier() {
    const next = tierDraft.trim()
    const value = next.length > 0 ? next : null
    if (value === (ctx.tier_name ?? null)) return
    try {
      await changeTier.mutateAsync(value)
      toast.success(value ? `Tier set to ${value}` : 'Tier cleared')
    } catch (err) {
      const msg =
        err instanceof OwnerApiError ? err.userMessage : 'Could not change tier'
      toast.error(msg)
    }
  }

  async function doRenew() {
    try {
      const res = await renew.mutateAsync()
      const until = formatExpiry(res.expires_at)
      toast.success(until ? `Renewed. Valid until ${until}` : 'Membership renewed')
    } catch (err) {
      if (err instanceof OwnerApiError && err.code === 'MEMBERSHIP_NOT_RENEWABLE') {
        // This program has no relative term — renewal isn't possible. Disable
        // the button + explain rather than leaving a dead action around.
        setRenewDisabled(true)
        toast.error('This membership has no renewable term.')
        return
      }
      const msg =
        err instanceof OwnerApiError ? err.userMessage : 'Could not renew'
      toast.error(msg)
    }
  }

  return (
    <div className="membership-detail">
      <div className="membership-detail-head">
        <h3>Membership</h3>
        <StatusPill status={ctx.status} />
      </div>

      <div className="membership-detail-grid">
        <div className="membership-detail-cell">
          <p className="membership-detail-label">Tier</p>
          <p className="membership-detail-value">{ctx.tier_name ?? '—'}</p>
        </div>
        <div className="membership-detail-cell">
          <p className="membership-detail-label">{ctx.member_number_label}</p>
          <p className="membership-detail-value membership-detail-number">
            {ctx.member_number}
          </p>
        </div>
        <div className="membership-detail-cell">
          <p className="membership-detail-label">
            {ctx.status === 'expired' ? 'Expired' : 'Valid until'}
          </p>
          <p className="membership-detail-value">{expiry ?? 'No expiry'}</p>
        </div>
        <div className="membership-detail-cell">
          <p className="membership-detail-label">Total check-ins</p>
          <p className="membership-detail-value">{ctx.check_in_count}</p>
        </div>
        <div className="membership-detail-cell">
          <p className="membership-detail-label">Last visit</p>
          <p className="membership-detail-value">{lastVisit ?? 'First visit'}</p>
        </div>
      </div>

      {ctx.benefits.length > 0 && (
        <div className="membership-detail-benefits">
          <p className="membership-detail-benefits-label">Benefits</p>
          <ul>
            {ctx.benefits.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="membership-detail-controls">
        {ctx.tiers_enabled && ctx.tier_options.length > 0 && (
          <div className="membership-tier-form">
            <label
              htmlFor="membership-tier-input"
              className="membership-detail-label"
            >
              Change tier
            </label>
            <div className="membership-tier-row">
              <select
                id="membership-tier-input"
                className="owner-input"
                value={tierDraft}
                onChange={(e) => setTierDraft(e.target.value)}
              >
                <option value="">No tier</option>
                {/* Keep the current tier selectable even if the owner has since
                    removed it from the program list, so it isn't silently lost. */}
                {ctx.tier_name && !ctx.tier_options.includes(ctx.tier_name) && (
                  <option value={ctx.tier_name}>{ctx.tier_name} (current)</option>
                )}
                {ctx.tier_options.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="owner-btn"
                onClick={saveTier}
                disabled={
                  changeTier.isPending ||
                  tierDraft.trim() === (ctx.tier_name ?? '')
                }
              >
                {changeTier.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          className="owner-btn owner-btn-block"
          onClick={doRenew}
          disabled={renew.isPending || renewDisabled}
        >
          {renewDisabled
            ? 'Not renewable'
            : renew.isPending
              ? 'Renewing…'
              : 'Renew membership'}
        </button>
      </div>
    </div>
  )
}
